import useWindowSize from "../../../hooks/use-window-size";
import React, {useEffect, useLayoutEffect} from "react";
import {setAttributes} from "../../../../core/services/utils";
import debounce from "lodash.debounce";
import {maxHeight, minHeight, lineHeight, lineWidth, lineContainerHeight, changeLineColorsWaitDuration, changeLineNumbersWaitDuration} from "../index";


const FormSection = ({selectedKey, children, containerRef}) => {
    const windowSize = useWindowSize();

    /**
     * Listens for the changes in the selected key and with each change, changes the color of the lines to the color
     * fo the selected key
     */
    useEffect(() => {
        const lines = document.getElementsByClassName('line');
        const container = containerRef?.current;
        if (lines?.length <= 0 || !container) return;
        changeLineColors(lines, container);
        return () => changeLineColors.cancel();
    }, [containerRef?.current, selectedKey]);

    /**
     * Listens for the changes in the size of the window and with each change, invokes the moveLines function with a
     * debounced property.
     */
    useEffect(() => {
        const lines = document.getElementsByClassName('line');
        const container = containerRef?.current;
        if (lines?.length <= 0 || !container) return;
        changeLinePositions(lines, container);
        return () => changeLinePositions.cancel();
    }, [containerRef?.current, windowSize.width, windowSize.height]);

    /**
     * As soon as the component renders, creates the background lines for the login form section
     */
    useLayoutEffect(() => {
        if (!containerRef.current)
            return;
        createLines();
    }, [containerRef.current]);


    /**
     * Creates a single line and returns it
     * @param containerWidth {number}
     * @param index {number}
     */
    const createLine = (index, containerWidth) => {
        const div = document.createElement('div');
        setAttributes(div,
            {
                style: {
                    position: 'absolute',
                    top: `${(index * lineContainerHeight) + ((lineContainerHeight - lineHeight) / 2)}px`,
                    left: `${(Math.random() * (containerWidth - lineWidth - 5))}px`,
                    backgroundColor: selectedKey.color,
                    width: `${lineWidth}px`,
                    height: `${lineHeight}px`,
                    zIndex: 1,
                }
            });
        div.classList.add('rounded-md', 'transition', 'line');
        return div
    }

    /**
     * Creates the lines of bizkey.json tech login page for the first time. and appends them to element with the
     * container containerRef.
     */
    const createLines = () => {
        const container = containerRef.current;
        const w = container.clientWidth;
        const h = container.clientHeight;
        const numberOfLines = Math.trunc(h / lineContainerHeight);
        for (let index = 0; index < numberOfLines; ++index) {
            container.appendChild(createLine(index, w));
        }
    }

    /**
     * Moves the lines of the page when the user changes the size of them
     * if there is a vertical change in the window, will create or remove lines accordingly to fit the screen
     * @type {function}
     */
    const changeLinePositions = debounce((lines, container) => {
        const w = container.clientWidth;
        const h = container.clientHeight;
        const numberOfNewLines = Math.trunc(h / lineContainerHeight);
        // remove extra lines from the end when there are extra lines
        if (numberOfNewLines < lines?.length) {
            for (let index = lines?.length - 1; index > numberOfNewLines; --index) {
                try {
                    container.removeChild(lines.item(index));
                } catch (e) {
                }
            }
        }
        // add extra lines to the end when there are less lines
        if (numberOfNewLines > lines?.length) {
            for (let index = lines?.length; index < numberOfNewLines; ++index) {
                container.appendChild(createLine(index, w));
            }
        }
        // change the position of each line
        for (let index = 0; index < lines?.length; ++index) {
            let newLeft = (Math.random() * (w - lineWidth));
            if (index > 0) {
                const prev = lines.item(index - 1);
                const prevLeft = parseFloat(prev.style.left.substring(0, prev.style.left.length - 2));
                while ((prevLeft + lineWidth >= newLeft && prevLeft + lineWidth <= newLeft + lineWidth) || (prevLeft >= newLeft && prevLeft <= newLeft + lineWidth)) {
                    newLeft = (Math.random() * (w - lineWidth));
                }
            }
            lines.item(index).style.left = `${newLeft}px`;
        }
    }, changeLineNumbersWaitDuration);

    /**
     * Changes the color of the lines depending on the selected key color
     * @type {function}
     */
    const changeLineColors = debounce((lines) => {
        for (let index = 0; index < lines?.length; ++index) {
            lines.item(index).style.backgroundColor = selectedKey.color;
        }
    }, changeLineColorsWaitDuration);

    return (
        <>
            <div className={'h-100 z-index-100'}
                 style={{
                     minHeight: minHeight,
                     maxHeight: maxHeight,
                 }}
            >
                <div className={'overflow-hidden bg-white'}
                     style={{marginTop: 60}}
                >
                    {children}
                </div>
            </div>
        </>
    );
}


export default FormSection;
